import { useCallback, useLayoutEffect, useEffect, useRef, useState } from 'react';
import { ATLAS_ICON } from './layoutTree';

const FOLLOW = 0.28;
const STACK_DX = 8;
const STACK_DY = 7;
const SETTLE_MS = 520;

const easeOutQuint = (t) => 1 - (1 - t) ** 5;

const glyphFractions = (el) => {
  const open = el?.classList.contains('atlas__node--expanded');
  return {
    top: open ? ATLAS_ICON.glyphTopOpen : ATLAS_ICON.glyphTopClosed,
    bot: open ? ATLAS_ICON.glyphBotOpen : ATLAS_ICON.glyphBotClosed,
  };
};

const edgePathFrom = (s, t) => {
  if (!s || !t) return '';
  const x1 = s.cx;
  const y1 = s.bottom;
  const x2 = t.cx;
  const y2 = t.top;
  const mid = (y1 + y2) / 2;
  return `M ${x1} ${y1} C ${x1} ${mid}, ${x2} ${mid}, ${x2} ${y2}`;
};

const clonePosMap = (map) => {
  const next = new Map();
  map.forEach((pos, id) => next.set(id, { x: pos.x, y: pos.y }));
  return next;
};

/**
 * Drag: JS-follow a stack under the cursor.
 * Release: ease every node from its live (stack) position into the current layout.
 * React keeps reading live positions through the settle so it cannot snap.
 */
export function useAtlasNodeMotion({
  layoutNodes,
  edges,
  onMeasuredHeights,
  zoom = 1,
  zoomRef: zoomRefProp = null,
}) {
  const liveRef = useRef(new Map());
  const nodeElsRef = useRef(new Map());
  const edgeElsRef = useRef(new Map());
  const layoutRef = useRef(layoutNodes);
  const edgesRef = useRef(edges);
  const zoomRef = useRef(zoom);
  if (zoomRefProp) zoomRef.current = zoomRefProp.current || zoom;
  else zoomRef.current = zoom;
  const rafRef = useRef(0);
  const dragRef = useRef({ ids: [], cursor: null });
  const settlingRef = useRef(false);
  const settleFromRef = useRef(new Map());
  const settleStartRef = useRef(0);
  const followFnRef = useRef(() => {});
  const settleFnRef = useRef(() => {});
  const [, setFrame] = useState(0);

  layoutRef.current = layoutNodes;
  edgesRef.current = edges;

  const bump = () => setFrame((n) => n + 1);

  const stopRaf = () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
  };

  const glyphAttach = useCallback((id, pos) => {
    const el = nodeElsRef.current.get(id);
    const z = (zoomRefProp && zoomRefProp.current) || zoomRef.current || 1;
    const icon = el?.querySelector('[data-atlas-icon]');
    const svg = icon?.querySelector('svg');
    const { top: topF, bot: botF } = glyphFractions(el);

    // Screen rects already include zoom / border / the 48×48 SVG hang.
    // Divide by zoom once to get world deltas from the node's border-box.
    if (el && svg) {
      const nodeRect = el.getBoundingClientRect();
      const svgRect = svg.getBoundingClientRect();
      if (svgRect.height > 1 && nodeRect.height > 1) {
        const svgX = (svgRect.left - nodeRect.left) / z;
        const svgY = (svgRect.top - nodeRect.top) / z;
        const svgW = svgRect.width / z;
        const svgH = svgRect.height / z;
        return {
          cx: pos.x + svgX + svgW / 2,
          top: pos.y + svgY + svgH * topF,
          bottom: pos.y + svgY + svgH * botF,
        };
      }
    }

    const left = icon ? icon.offsetLeft : ATLAS_ICON.padX;
    const top = icon ? icon.offsetTop : ATLAS_ICON.padY;
    const w = icon ? icon.offsetWidth : ATLAS_ICON.width;
    const h = icon ? icon.offsetHeight : ATLAS_ICON.height;
    const borderY = el?.clientTop || 0;
    const svgH = 48;
    const svgY = top + borderY + (h - svgH) / 2;
    return {
      cx: pos.x + left + w / 2,
      top: pos.y + svgY + svgH * topF,
      bottom: pos.y + svgY + svgH * botF,
    };
  }, []);

  const applyEdges = useCallback(() => {
    const live = liveRef.current;
    const layoutById = new Map(layoutRef.current.map((n) => [n.id, n]));
    const dragging = dragRef.current.ids;
    for (const edge of edgesRef.current || []) {
      const pathEl = edgeElsRef.current.get(edge.id);
      if (!pathEl) continue;
      const sN = layoutById.get(edge.source);
      const tN = layoutById.get(edge.target);
      if (!sN || !tN) continue;
      const involved =
        dragging.includes(edge.source) || dragging.includes(edge.target);
      const sPos = involved ? { x: sN.x, y: sN.y } : live.get(edge.source) || sN;
      const tPos = involved ? { x: tN.x, y: tN.y } : live.get(edge.target) || tN;
      pathEl.setAttribute(
        'd',
        edgePathFrom(glyphAttach(edge.source, sPos), glyphAttach(edge.target, tPos))
      );
      pathEl.classList.toggle('atlas__edge--ghost', involved);
    }
  }, [glyphAttach]);

  const applyNodes = useCallback(() => {
    for (const n of layoutRef.current) {
      let pos = liveRef.current.get(n.id);
      if (!pos) {
        pos = { x: n.x, y: n.y };
        liveRef.current.set(n.id, pos);
      }
      const el = nodeElsRef.current.get(n.id);
      if (el) {
        el.style.transition = 'none';
        el.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
      }
    }
  }, []);

  const tickFollow = useCallback(() => {
    const { ids, cursor } = dragRef.current;
    if (!cursor || ids.length === 0) return;
    ids.forEach((id, i) => {
      const pos = liveRef.current.get(id);
      if (!pos) return;
      pos.x += (cursor.x - ATLAS_ICON.centerX + i * STACK_DX - pos.x) * FOLLOW;
      pos.y += (cursor.y - ATLAS_ICON.padY - ATLAS_ICON.height / 2 + i * STACK_DY - pos.y) * FOLLOW;
    });
    applyNodes();
    applyEdges();
    bump();
    rafRef.current = requestAnimationFrame(() => followFnRef.current());
  }, [applyNodes, applyEdges]);

  const tickSettle = useCallback(() => {
    if (!settlingRef.current) return;
    const u = Math.min(1, (performance.now() - settleStartRef.current) / SETTLE_MS);
    const e = easeOutQuint(u);
    const fromMap = settleFromRef.current;

    for (const n of layoutRef.current) {
      const from = fromMap.get(n.id) || n;
      liveRef.current.set(n.id, {
        x: from.x + (n.x - from.x) * e,
        y: from.y + (n.y - from.y) * e,
      });
    }

    applyNodes();
    applyEdges();
    bump();

    if (u < 1) {
      rafRef.current = requestAnimationFrame(() => settleFnRef.current());
      return;
    }

    settlingRef.current = false;
    for (const n of layoutRef.current) {
      liveRef.current.set(n.id, { x: n.x, y: n.y });
    }
    applyNodes();
    applyEdges();
    bump();
  }, [applyNodes, applyEdges]);

  followFnRef.current = tickFollow;
  settleFnRef.current = tickSettle;

  const applyEdgesRef = useRef(applyEdges);
  applyEdgesRef.current = applyEdges;
  const onHeightsRef = useRef(onMeasuredHeights);
  onHeightsRef.current = onMeasuredHeights;
  const resizeObsRef = useRef(null);
  const heightRafRef = useRef(0);

  const reportHeights = useCallback(() => {
    if (dragRef.current.ids.length || settlingRef.current) return;
    cancelAnimationFrame(heightRafRef.current);
    heightRafRef.current = requestAnimationFrame(() => {
      const next = new Map();
      nodeElsRef.current.forEach((el, id) => {
        next.set(id, el.offsetHeight);
      });
      if (next.size) onHeightsRef.current?.(next);
    });
  }, []);

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      applyEdgesRef.current();
      reportHeights();
    });
    resizeObsRef.current = ro;
    nodeElsRef.current.forEach((el) => ro.observe(el));
    return () => {
      cancelAnimationFrame(heightRafRef.current);
      ro.disconnect();
      resizeObsRef.current = null;
    };
  }, [reportHeights]);

  // At rest only: snap live positions to layout. Never during drag or settle.
  useLayoutEffect(() => {
    if (dragRef.current.ids.length || settlingRef.current) {
      applyEdges();
      return undefined;
    }
    for (const n of layoutNodes) {
      liveRef.current.set(n.id, { x: n.x, y: n.y });
    }
    applyNodes();
    applyEdges();
    return undefined;
  }, [layoutNodes, applyNodes, applyEdges]);

  const nodeCbCache = useRef(new Map());
  const edgeCbCache = useRef(new Map());

  const registerNode = useCallback((id) => {
    let cb = nodeCbCache.current.get(id);
    if (!cb) {
      cb = (el) => {
        if (el) {
          nodeElsRef.current.set(id, el);
          resizeObsRef.current?.observe(el);
          const pos = liveRef.current.get(id);
          if (pos) {
            el.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
          }
        } else {
          const prev = nodeElsRef.current.get(id);
          if (prev) resizeObsRef.current?.unobserve(prev);
          nodeElsRef.current.delete(id);
        }
      };
      nodeCbCache.current.set(id, cb);
    }
    return cb;
  }, []);

  const registerEdge = useCallback((id) => {
    let cb = edgeCbCache.current.get(id);
    if (!cb) {
      cb = (el) => {
        if (el) edgeElsRef.current.set(id, el);
        else edgeElsRef.current.delete(id);
      };
      edgeCbCache.current.set(id, cb);
    }
    return cb;
  }, []);

  const startDrag = useCallback(
    (ids) => {
      settlingRef.current = false;
      stopRaf();
      dragRef.current = { ids: [...ids], cursor: null };
      applyEdges();
    },
    [applyEdges]
  );

  const moveDrag = useCallback((world) => {
    if (!dragRef.current.ids.length) return;
    const wasIdle = !dragRef.current.cursor;
    dragRef.current.cursor = world;
    if (wasIdle) {
      stopRaf();
      rafRef.current = requestAnimationFrame(() => followFnRef.current());
    }
  }, []);

  const endDrag = useCallback(() => {
    dragRef.current = { ids: [], cursor: null };
    stopRaf();
    settleFromRef.current = clonePosMap(liveRef.current);
    settleStartRef.current = performance.now();
    settlingRef.current = true;
    rafRef.current = requestAnimationFrame(() => settleFnRef.current());
  }, []);

  const livePos = (id) => liveRef.current.get(id);

  return { registerNode, registerEdge, startDrag, moveDrag, endDrag, livePos };
}

export const ATLAS_STACK = { dx: STACK_DX, dy: STACK_DY };
